import type { ReactElement } from 'react';
import { StatusDot } from '../StatusDot';

/**
 * How far back a Claude Desktop session descriptor still counts, in days.
 *
 * Mirrors the backend's `INVENTORY_SESSION_TTL_DAYS`
 * (`db/repositories/user-mcp-configurations.repository.ts`), which is the
 * window `coworkInventoryVisible()` uses to decide whether a session row is
 * surfaced at all. That constant is backend-only — it is a SQL-visibility rule,
 * not part of the API contract — so it is restated here purely as tooltip
 * wording. Keep the two in step; a drift only misstates the window in prose,
 * it cannot change which rows are returned.
 */
const MCP_SESSION_WINDOW_DAYS = 7;

interface MCPStatusIndicatorProps {
  runningCount: number;
  /**
   * How many of `runningCount`'s instances had their running state derived from
   * a declared tool list rather than measured by an MCP handshake (KIR-4645).
   * Omitted by callers that have no such attribution.
   */
  derivedRunningCount?: number | null;
}

/**
 * Component for displaying MCP server running status with tooltip
 */
export function MCPStatusIndicator({
  runningCount,
  derivedRunningCount,
}: MCPStatusIndicatorProps): ReactElement {
  const isActive = runningCount > 0;

  // KIR-4645. Claude Desktop connectors are reachable only through Claude
  // Desktop, so the agent cannot handshake them and their running state is
  // derived from the tool list the session descriptor declares — which Desktop
  // only writes once the user has actually connected the server (KIR-2984).
  // That makes Active the right badge, but "3 instances currently running"
  // would be a claim about a live probe we never performed. Disclose the
  // difference only when EVERY running instance is derived: a server with even
  // one handshaked instance has been genuinely measured, and the ordinary
  // instance count is the more useful thing to say about it.
  const isDerivedOnly =
    isActive &&
    derivedRunningCount != null &&
    derivedRunningCount >= runningCount;

  const plural = runningCount !== 1 ? 's' : '';

  let tooltip: string;
  if (!isActive) {
    tooltip = 'No instances currently running';
  } else if (isDerivedOnly) {
    // Keeps the instance count — on an org-wide row it is the difference
    // between one laptop and forty — and scopes "connected" to the inventory
    // window rather than to now. A session descriptor is a frozen snapshot
    // Claude Desktop never rewrites, so a connector the user has since removed
    // stays Active until its descriptor ages out of INVENTORY_SESSION_TTL_DAYS.
    // Saying "connected" unqualified would read as a live claim.
    tooltip = `${runningCount} instance${plural} connected in a Claude Desktop session in the last ${MCP_SESSION_WINDOW_DAYS} days; status derived from published tools, not a live handshake.`;
  } else {
    tooltip = `${runningCount} instance${plural} currently running`;
  }

  return (
    <StatusDot
      isActive={isActive}
      activeLabel="Active"
      inactiveLabel="Inactive"
      tooltip={tooltip}
      align="start"
    />
  );
}
